import styles from './App.module.scss';

import {
  CheckPolloverse,
  Header,
  LanguageSwitcher,
  LoadingScreen,
  Onboarding,
  TermsModal,
  ToastProvider,
} from '@/components';
import onboardingVersions from '@/configs/onboarding.json';
import { ScrollProvider, useSetLanguage } from '@/hooks';
import { useNavigation } from '@/hooks/useNavigation';
import { PageContainer } from '@/layout/PageContainer';
import { ErrorFallback } from '@/modules/error/ErrorFallback';
import { BalanceUpdateNotification } from '@/modules/notification/components/BalanceUpdateNotification';
import { CommonNotification } from '@/modules/notification/components/CommonNotification';
import { NOTIFICATION_TYPE } from '@/modules/notification/types';
import { useStore } from '@/store';
import { analytics } from '@/utils/analytics';
import { HELIKA_EVENT_TYPE } from '@/utils/helika';
import { isTelegramApp } from '@/utils/isTelegramApp';
import { shouldShowOnboarding } from '@/utils/shouldShowOnboarding';
import { storage } from '@/utils/storage';
import cn from 'classnames';
import { Tabs } from 'components/Tabs';
import { useScroll } from 'framer-motion';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  WalletId,
  WalletManager,
  WalletProvider,
} from '@txnlab/use-wallet-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';

import { LoginModalProvider } from './LoginModalContext';

console.log('import.meta.env.VITE_IS_STAGING', import.meta.env.VITE_IS_STAGING);
// const network = import.meta.env.VITE_IS_STAGING ? 'testnet' : 'mainnet';
const network = 'mainnet';
console.log('Selected network:', network);

const manager = new WalletManager({
  wallets: [WalletId.PERA, WalletId.DEFLY],
  defaultNetwork: network,
  networks: {
    mainnet: {
      algod: {
        baseServer: import.meta.env.VITE_ALGOD_SERVER,
        port: import.meta.env.VITE_ALGOD_PORT,
        token: import.meta.env.VITE_ALGOD_TOKEN,
      },
      caipChainId: 'algorand:wGHE2Pwdvd7S12BL5FaOP20EGYesN73k',
    },
    testnet: {
      algod: {
        baseServer: import.meta.env.VITE_ALGOD_SERVER,
        port: import.meta.env.VITE_ALGOD_PORT,
        token: import.meta.env.VITE_ALGOD_TOKEN,
      },
      caipChainId: 'algorand:SGO1GKSzyE7IEPItTxCByw9x8FmnrCDe',
    },
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
    },
  },
});

const VITE_POLLOVERSE_CHECK_COUNTDOWN_IN_HOURS = Number(
  import.meta.env.VITE_POLLOVERSE_CHECK_COUNTDOWN_IN_HOURS ?? 24,
);

export const App = observer(() => (
  <BrowserRouter>
    <QueryClientProvider client={queryClient}>
      <WalletProvider manager={manager}>
        <LoginModalProvider>
          <AppContent />
        </LoginModalProvider>
      </WalletProvider>
    </QueryClientProvider>
  </BrowserRouter>
));

function isObjectEmpty(obj: unknown): boolean {
  return (
    typeof obj === 'object' && obj !== null && Object.keys(obj).length === 0
  );
}

const AppContent = observer(() => {
  const {
    pageStore,
    accountStore: {
      updateAccount,
      authorizationToken,
      accountData,
      setYandexMetrikaParams,
    },
    walletStore: { getJettonsList, getJettonBalances },
    errorStore: { error },
    notificationStore: {
      notificationType,
      setNotificationType,
      setNotificationMessage,
    },
    startParams,
  } = useStore();

  useSetLanguage();

  const ref = useRef<HTMLDivElement | null>(null);
  const { scrollYProgress } = useScroll({ container: ref });
  const { currentRoute } = useNavigation();

  const [isPolloverseShown, setIsPolloverseShown] = useState(false);
  const [isLanguageModalOpen, setLanguageModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitAnimationEnded, setIsInitAnimationEnded] = useState(false);
  const [isTermsModalShown, setIsTermsModalShown] = useState(false);

  const isAuthenticated = Boolean(accountData._id);
  const showOnboarding = shouldShowOnboarding(
    accountData,
    'general',
    pageStore,
  );

  useEffect(() => {
    const showTerms = shouldShowOnboarding(accountData, 'terms', pageStore);
    setIsTermsModalShown(showTerms);
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && startParams && !isObjectEmpty(startParams)) {
      const handleUpdateAccount = async () => {
        const { invite_code, ...rest } = startParams;
        const res = await updateAccount(
          invite_code ? { invite_code, utm: rest } : { utm: rest },
        );
        if (res.isError) {
          setNotificationMessage(res.message);
          setNotificationType(NOTIFICATION_TYPE.COMMON);
        }
      };
      handleUpdateAccount();
    }
  }, [startParams, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      if (Number(import.meta.env.VITE_YANDEX_METRIKA_ID)) {
        setYandexMetrikaParams(accountData);
      }
      analytics.sendEvent({
        event_type: HELIKA_EVENT_TYPE.LOADING,
        event: {
          event_sub_type: 'account_login',
          event_details: {
            event_label: 'Account login',
            event_action: 'login',
          },
          ...startParams,
        },
      });
      analytics.sendEvent({
        event_type: HELIKA_EVENT_TYPE.GAMEPLAY,
        event: {
          event_sub_type: 'game_start',
          event_details: {
            event_label: 'Game start',
            event_action: 'start',
          },
          ...startParams,
        },
      });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const handleFetchData = async () => {
      await getJettonsList();
      await getJettonBalances();

      setIsLoading(false);

      const interval = setInterval(() => {
        getJettonBalances();
      }, 10000);

      return () => clearInterval(interval);
    };

    const handleFetchJettonsList = async () => {
      await getJettonsList();
      setIsLoading(false);
    };

    if (authorizationToken) {
      handleFetchData();
    } else {
      handleFetchJettonsList();
    }
  }, [authorizationToken]);

  useEffect(() => {
    const setLastShownTime = async () => {
      const storageKey = 'lastShownTime';
      const currentTime = new Date().getTime();
      const lastShownTime = await storage.getItem(storageKey);

      if (!lastShownTime) {
        setIsPolloverseShown(true);
        await storage.setItem(storageKey, currentTime.toString());
        return;
      }

      const timeDiff = currentTime - Number(lastShownTime);
      const hoursDiff = timeDiff / (1000 * 60 * 60);

      if (hoursDiff >= VITE_POLLOVERSE_CHECK_COUNTDOWN_IN_HOURS) {
        setIsPolloverseShown(true);
        await storage.setItem(storageKey, currentTime.toString());
      }
    };

    setLastShownTime();
  }, []);

  useEffect(() => {
    const checkTermsAcceptance = async () => {
      const termsAccepted = await storage.getItem('termsAccepted');
      if (!termsAccepted && isAuthenticated) {
        setIsTermsModalShown(true);
      }
    };

    if (isAuthenticated && !isLoading) {
      checkTermsAcceptance();
    }
  }, [isAuthenticated, isLoading]);

  const onTermsClose = () => {
    setIsTermsModalShown(false);
    updateAccount({ onboarding: { terms: onboardingVersions.terms } });
  };

  return (
    <div
      id="app"
      data-platform={isTelegramApp() ? 'telegram' : 'web'}
      className={cn(styles.app, currentRoute?.hideTabs && styles.fullHeight)}
      ref={ref}
    >
      {error.length > 0 ? (
        <ErrorFallback error={error} />
      ) : isLoading || !isInitAnimationEnded ? (
        <LoadingScreen
          setIsInitAnimationEnded={setIsInitAnimationEnded}
          isLoading={isLoading}
        />
      ) : (
        <>
          <ScrollProvider value={{ scrollYProgress }}>
            <Header />
            <PageContainer />
            {showOnboarding && <Onboarding />}
            {isPolloverseShown && isAuthenticated && !showOnboarding && (
              <CheckPolloverse setIsOpen={setIsPolloverseShown} />
            )}
          </ScrollProvider>
          <ToastProvider />
        </>
      )}

      {!currentRoute?.hideTabs && <Tabs />}

      {notificationType === NOTIFICATION_TYPE.COMMON && <CommonNotification />}
      {notificationType === NOTIFICATION_TYPE.BALANCE_CHANGES &&
        !(isLoading || !isInitAnimationEnded) && <BalanceUpdateNotification />}
      {isLanguageModalOpen && (
        <LanguageSwitcher onClose={() => setLanguageModalOpen(false)} />
      )}
      <TermsModal
        isVisible={isTermsModalShown}
        onClose={() => onTermsClose()}
      />
    </div>
  );
});
